import { Menu, Form, Col, Select } from "antd";
import { AiOutlineCar, AiOutlineDelete, AiOutlineHistory, AiOutlinePrinter } from "react-icons/ai";
import { FaTemperatureHigh } from "react-icons/fa";
import { CASH_ID, isAccessible, isRtoWaybill, UserEnum } from "../../utils/app-common";
import { SessionStorage } from "../../services/storage";
import { NavigateFunction, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { IOperationState } from "../../model/OperationModel";
import { ICommonState, IFilter } from "../../model/CommonModel";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { useBookingStore } from "../../stores/BookingStore";
import { IBookingState } from "../../model/BookingModel";
import { useOperationStore } from "../../stores/OperationStore";
import { useTrackingHistoryStore } from "../../stores/TrackingHistoryStore";
import { IOperation, ITrackingHistoryState } from "../../model/TrackingHistoryModal";
import { useCommonStore } from "../../stores/CommonStore";
import { TbListDetails } from "react-icons/tb";
import { GrMapLocation } from "react-icons/gr";
import ConfirmationModal from "../CommonComponent/ConfirmationModal";
import { cancelLabel, rtoCancelLabel, rtoLabel } from "../../common/common";
import { requiredValidation } from "../../validation/validation";
import React from "react";
import { RealtimetempratureComponent } from "../TZoneDeviceComponent/RealtimeTempratureComponent";
import { PieceBarcodeComponent } from "../OperationComponent/MenuListComponent";

type MenuListProps = {
    row: IOperation;
    setIsModalOpen: (isOpen: boolean) => void;
    pageSize: number;
    pageNumber: number;
    filteredData: IFilter;
    selectedRowKeys: React.Key[];
};

export const MenuList = ({
    row,
    setIsModalOpen,
    pageNumber = 1,
    pageSize,
    filteredData,
}: MenuListProps) => {
    const navigate: NavigateFunction = useNavigate();

    const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
    const [isTempratureTrackingModalOpen, setIsTempratureTrackingModalOpen] =
        useState(false);
    const [isConfirmationModal, setIsConfirmationModal] =
        useState<boolean>(false);
    const [isConfirmationPieceModal, setIsConfirmationPieceModal] =
        useState<boolean>(false);
    const [isRTOConfirmationModal, setIsRTOConfirmationModal] =
        useState<boolean>(false);
    const [form] = Form.useForm();
    const { cancelBookingExceptionList } = JSON.parse(
        SessionStorage.get("app-storage") ?? "{}"
    );
    const { promiseInProgress: rtoProgress } = usePromiseTracker({
        area: "rto-area",
    });
    const CancelBookingByIdAction = useBookingStore(
        (state: IBookingState) => state?.CancelBookingByIdAction
    );
    const RTOByIdAction = useOperationStore(
        (state: IOperationState) => state?.RTOByIdAction
    );
    const ClearTrackingStore = useTrackingHistoryStore(
        (state: ITrackingHistoryState) => state?.ClearTrackingStore
    );
    const ClearTemperatureTrackingStore = useTrackingHistoryStore(
        (state: ITrackingHistoryState) => state?.ClearTemperatureTrackingStore
    );
    const TrackWayBillAction = useBookingStore(
        (state: IBookingState) => state?.TrackWayBillAction
    );
    const rtoResponse = useOperationStore((state) => state?.rtoResponse);

    const PrintCashRecieptAction = useCommonStore(
        (state: ICommonState) => state?.PrintCashRecieptAction
    );
    const PrintIRSReportAction = useCommonStore(
        (state: ICommonState) => state?.PrintIRSReportAction
    );

    const onPrintCashRecieptHandle = useCallback(() => {
        trackPromise(
            PrintCashRecieptAction(row?.waybillNo),
            "operation-print-area"
        );
    }, [PrintCashRecieptAction, row?.waybillNo]);

    const onPrintIRSReportHandler = () => {
        trackPromise(PrintIRSReportAction(row?.waybillNo), "operation-print-area");
    };

    const onPrintPieceBarcodeHandle = () => setIsConfirmationPieceModal(true);

    useEffect(() => {
        if (rtoResponse) {
            setIsRTOConfirmationModal(false);
            useOperationStore.setState({ rtoResponse: undefined });
        }
    }, [rtoResponse]);

    const onTrackingHistoryHandle = useCallback((row) => {
        ClearTrackingStore();
        useTrackingHistoryStore.setState({ rowDetail: row });
        navigate(`/operation/tracking-history/${row?.waybillNo}`);
    }, [ClearTrackingStore, navigate]);

    const onTempratureTrackingHandle = useCallback(() => {
        ClearTemperatureTrackingStore();
        setIsTempratureTrackingModalOpen(true);
    }, [ClearTemperatureTrackingStore]);

    const onCancelOperationHandle = useCallback(() => {
        setIsConfirmationModal(true);
    }, []);
    const onRTOConfirmationHandler = useCallback(() => {
        trackPromise(
            RTOByIdAction(row?.waybillNo, {
                ...filteredData,
                count: pageSize,
                startIndex: pageNumber,
            }),
            "rto-area"
        );
    }, [RTOByIdAction, filteredData, pageNumber, pageSize, row?.waybillNo]);

    const onRTOHandle = useCallback(() => {
        setIsRTOConfirmationModal(true);
    }, []);

    const isRto = isRtoWaybill(row?.waybillNo);

    const onConfirmedClicked = useCallback(() => {
        // RTO cancel API does not take a reason
        if (!isRto && !form?.getFieldValue("cancelReasonId")) return false;
        trackPromise(
            CancelBookingByIdAction(
                row?.id,
                { ...filteredData, count: pageSize, startIndex: pageNumber },
                isRto ? undefined : {
                    cancelReasonId: form?.getFieldValue("cancelReasonId"),
                },
                undefined,
                row?.waybillNo
            )
        );
        setIsConfirmationModal(false);
    }, [
        CancelBookingByIdAction,
        row?.id,
        row?.waybillNo,
        isRto,
        filteredData,
        pageSize,
        pageNumber,
        form,
    ]);

    const onTrackWayBill = useCallback(
        (waybillNo: string | undefined) => {
            trackPromise(TrackWayBillAction(waybillNo!), "tracking-way-bill");
        },
        [TrackWayBillAction]
    );
    const { pathname } = useLocation();

    const onDetailHandler = (record: IOperation) => {
        const {
            id,
            isPickedup,
            isPickupAck,
            isDelivered,
            lastStatus,
            productName,
            subProduct,
            temperature,
            shipperName,
            consigneeName,
            originCity,
            deliveryDate,
            assignVehicle,
            isIrs,
            originCountry,
            pickupDriverName,
            pickupDriverId,
            deliveryDriverName,
            waybillNo,
            bookingCode,
            billTypeId,
            billType,
            invoiced,
            isRTO,
            originalWaybill,
            bookingDate,
            pickupDate,
            isRescheduled,
            rtoWaybill,
            customerAccount,
            manifestConfirmed,
            isManualWaybill,
            productId,
            plateNumber,
            deliveryPlateNumber,
            deliveryDriverId,
            isDeliveryAck
        } = record || {};
        const fromOperation = ["/operation"].includes(pathname);

        const selectedData = {
            productId,
            isRescheduled,
            deliveryDate,
            rtoWaybill,
            isPickupAck,
            isPickedup: Boolean(isPickedup),
            isDeliveryAck,
            isDelivered: Boolean(isDelivered),
            billTypeId,
            isRto: Boolean(isRTO),
            lastStatus,
            productName,
            subProduct,
            temperature,
            shipperName,
            consigneeName,
            originCity,
            originCountry,
            pickupDriverName,
            pickupDriverId,
            deliveryDriverName,
            waybillNo,
            bookingCode,
            id,
            billType,
            originalWaybillNo: originalWaybill,
            isInvoiced: invoiced,
            isIrs: Boolean(isIrs),
            bookingDate,
            pickupDate,
            customerAccount,
            assignVehicle,
            fromOperation,
            plateNumber,
            deliveryPlateNumber,
            deliveryDriverId,
            isManualWaybill: Boolean(isManualWaybill) && !isDelivered,
            manifestConfirmed: Boolean(manifestConfirmed),
        };

        SessionStorage.setObject("selectedOperation", selectedData);
        navigate(`/operation/${id}`, {
            state: selectedData,
        });
    };

    const hasNoAccess =
        isAccessible([
            UserEnum.SALES_DIRECTOR,
            UserEnum.MANIFESTER,
            UserEnum.OPERATION_CS_SUPERVISOR_MANIFESTER,
            UserEnum.DATA_ANALYST, UserEnum.OCC_SOLUTION,
            UserEnum.FINANCE,
            UserEnum.OPERATION_SUPERVISOR,
            UserEnum.CUSTOMER_SERVICE,
            UserEnum.SALES_MANAGER,
            UserEnum.FINANCE_CONTROLLER,
            UserEnum.DATA_LOGGER,
            UserEnum.OPERATION_SUPERVISOR_OPERATION_STAFF,
        ]) || row?.isDelivered;
    const isRTOAccessible = !hasNoAccess;
    const noAccessForMenuDetail = isAccessible([
        UserEnum.FINANCE,
        UserEnum.SALES_MANAGER,
        UserEnum.FINANCE_CONTROLLER,
        UserEnum.DATA_LOGGER,
    ]);
    const rtoMenuItem = {
        key: 4,
        label: "RTO",
        icon: <AiOutlineCar size={18} />,
        onClick: () => onRTOHandle(),
    };
    const isPickedUp = row?.isPickedup === "1";
    const trackTemperatureMenuItem = {
        key: 8,
        label: "Track Temperature",
        icon: <FaTemperatureHigh size={18} />,
        onClick: () => onTempratureTrackingHandle(),
    };

    return (
        <>
            <Menu
                onClick={() => setIsModalOpen(false)}
                items={[
                    {
                        key: 1,
                        label: "Details",
                        disabled: row?.isCancelled,
                        icon: <TbListDetails size={18} />,
                        onClick: () => onDetailHandler(row),
                    },

                    !hasNoAccess && !row?.isPickedup
                        ? {
                            // No = false
                            key: 3,
                            danger: true,
                            label: isRto ? "RTO Cancel" : "Cancel",
                            disabled: row?.isCancelled,
                            icon: <AiOutlineDelete size={18} />,
                            onClick: () => onCancelOperationHandle(),
                        }
                        : isRTOAccessible
                            ? rtoMenuItem
                            : null,
                    // {
                    //   key: 5,
                    //   label: 'Print Waybill',
                    //   title: 'Print Waybill',
                    //   icon: <AiOutlinePrinter size={18} />,
                    //   onClick: () => onPrintWayBillHandle(),
                    // disabled: Boolean(selectedRowKeys.length)

                    // }, {
                    //   key: 6,
                    //   label: 'Print Sticker',
                    //   title: 'Print Sticker',
                    //   icon: <AiOutlinePrinter size={18} />,
                    //   onClick: () => onPrintStickerHandle(),
                    // disabled: Boolean(selectedRowKeys.length)
                    // },
                    noAccessForMenuDetail || !Boolean(row?.isIrs)
                        ? null
                        : {
                            key: 10,
                            icon: <AiOutlinePrinter size={18} />,
                            label: "Print IRS Report",
                            onClick: () => onPrintIRSReportHandler(),
                        },
                    noAccessForMenuDetail
                        ? null
                        : {
                            key: 9,
                            icon: <AiOutlinePrinter size={18} />,
                            label: "Print Piece Barcode",
                            onClick: () => onPrintPieceBarcodeHandle(),
                        },
                    noAccessForMenuDetail ||
                        row?.billTypeId !== CASH_ID ||
                        !row?.isPickedup
                        ? null
                        : {
                            key: 10,
                            icon: <AiOutlinePrinter size={18} />,
                            label: "Print Cash Receipt",
                            onClick: () => onPrintCashRecieptHandle(),
                        },
                    noAccessForMenuDetail
                        ? null
                        : {
                            key: 7,
                            icon: <GrMapLocation size={18} />,
                            label: "Track Vehicle",
                            onClick: () => onTrackWayBill(row?.waybillNo),
                        },

                    noAccessForMenuDetail
                        ? null
                        : isPickedUp
                            ? trackTemperatureMenuItem
                            : null,
                ]}
            />
            {isConfirmationModal ? (
                <ConfirmationModal
                    openModal={isConfirmationModal}
                    confirmClicked={() => onConfirmedClicked()}
                    hideModal={() => setIsConfirmationModal(false)}
                    label={isRto ? rtoCancelLabel(row?.waybillNo) : cancelLabel(row?.bookingCode!)}
                    {...(isRto && { title: "Cancel RTO Waybill", okText: "Yes, Cancel", okType: "danger" as const })}
                >
                    <Form form={form} hidden={isRto}>
                        <Col>
                            <Form.Item
                                name="cancelReasonId"
                                rules={[requiredValidation]}
                                label="Reason"
                                labelCol={{ span: 24 }}
                            >
                                <Select
                                    showSearch
                                    allowClear
                                    filterOption={(input, option) =>
                                        (option?.children as unknown as string)
                                            ?.toLocaleLowerCase()
                                            ?.includes(input?.toLocaleLowerCase())
                                    }
                                >
                                    {cancelBookingExceptionList?.map((account) => (
                                        <React.Fragment key={account?.id}>
                                            <Select.Option key={account?.id} value={account?.id}>
                                                {account?.name}
                                            </Select.Option>
                                        </React.Fragment>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Form>
                </ConfirmationModal>
            ) : null}

            {isRTOConfirmationModal ? (
                <ConfirmationModal
                    promiseInProgress={rtoProgress}
                    openModal={isRTOConfirmationModal}
                    confirmClicked={() => onRTOConfirmationHandler()}
                    hideModal={() => setIsRTOConfirmationModal(false)}
                    label={rtoLabel(row?.bookingCode!)}
                />
            ) : null}



            {isTempratureTrackingModalOpen ? (
                <RealtimetempratureComponent
                    isOpen={isTempratureTrackingModalOpen}
                    closeModal={() => setIsTempratureTrackingModalOpen(false)}
                    data={row?.waybillNo}
                />
            ) : null}
            {isConfirmationPieceModal ? (
                <PieceBarcodeComponent
                    row={row}
                    isOpen={isConfirmationPieceModal}
                    onClose={(value) => setIsConfirmationPieceModal(value)}
                />
            ) : null}
        </>
    );
};